import { useMemo } from 'react';

import { isFinanceProject, isGlobalProject } from 'config';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import {
  PersonalDataSection,
  PlaceOfBirthSection,
  CitizenshipSection,
  TaxResidencySection,
  PepConnectionSection,
  ContactsSection,
  ActivitySection,
  IdentityDocumentSection,
  AdditionalDocumentsSection,
  KYCSection,
  ResidentialAddressSection,
  RegistrationAddressSection,
} from 'modules/client/common/components/sections';
import { CountryRiskField } from 'modules/client/common/feature';

import { ControlOverSection } from '../sections';

import { LegalClientBeneficiarySectionsProps } from './types';

export const useLegalClientBeneficiarySections = ({
  countries,
  countriesLoading,
  kycData,
  hideActivitySection,
  mainInfo,
}: LegalClientBeneficiarySectionsProps) => {
  const { t } = useTranslation();

  const isGlobal = isGlobalProject();
  const isFinance = isFinanceProject();

  const tabs = useMemo(
    () =>
      lodash.compact([
        {
          label: t('client.legal.legalClientBeneficiary.sections.main'),
          id: 'main',
          content: (
            <>
              <PersonalDataSection />
              <CitizenshipSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <IdentityDocumentSection
                enabledDocuments
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <TaxResidencySection
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <PlaceOfBirthSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
            </>
          ),
          formSectionKeys: [
            'personalData',
            'citizenships',
            'identityDocument',
            'taxResidences',
            'placeOfBirth',
          ],
        },
        {
          label: t(
            'client.legal.legalClientBeneficiary.sections.beneficiaryInfo'
          ),
          id: 'beneficiaryInfo',
          content: (
            <ControlOverSection
              enabledControlOverLegalClientStatus={isGlobal}
              enabledFinancialConditionAmount={isFinance}
              enabledHasControlOverLegalClient={isFinance}
            />
          ),
          formSectionKeys: ['controlOver'],
        },
        {
          label: t('client.legal.legalClientBeneficiary.sections.contacts'),
          id: 'contacts',
          content: (
            <>
              {isFinance && (
                <RegistrationAddressSection
                  countries={countries}
                  countriesLoading={countriesLoading}
                />
              )}
              <ResidentialAddressSection
                enabledFiles
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <ContactsSection hidePhoneField />
            </>
          ),
          formSectionKeys: [
            'residentialAddress',
            'registrationAddress',
            'contacts',
          ],
        },
        {
          label: t('client.legal.legalClientBeneficiary.sections.documents'),
          id: 'documents',
          content: <AdditionalDocumentsSection />,
          formSectionKeys: ['additionalDocumentsFiles'],
        },
        {
          label: t('client.legal.legalClientBeneficiary.sections.riskLevel'),
          id: 'riskLevel',
          content: (
            <>
              <KYCSection kycData={kycData} />
              <PepConnectionSection notRequestInformation={isGlobal} />
            </>
          ),
          formSectionKeys: ['additionalDocumentsFiles', 'pepConnection'],
        },
        {
          label: t('client.legal.legalClientBeneficiary.sections.other'),
          id: 'other',
          content: !hideActivitySection && <ActivitySection isBaseForm />,
          formSectionKeys: ['activity'],
        },
      ]),
    [
      countries,
      countriesLoading,
      hideActivitySection,
      isFinance,
      isGlobal,
      kycData,
      t,
    ]
  );

  const info = useMemo(
    () =>
      mainInfo
        ? lodash.compact([
            isGlobal && [
              {
                label: t(
                  'client.legal.legalClientBeneficiary.mainInfo.citizenship'
                ),
                value: mainInfo.citizenship?.name,
                countryRisk: {
                  country: mainInfo.citizenship,
                  field: CountryRiskField.Citizenship,
                },
              },
            ],
            [
              {
                label: t('client.legal.legalClientBeneficiary.mainInfo.email'),
                value: mainInfo.email,
              },
            ],
            [
              {
                label: t(
                  'client.legal.legalClientBeneficiary.mainInfo.country'
                ),
                value: mainInfo.country?.name,
                countryRisk: {
                  country: mainInfo.country,
                  field: mainInfo.countryRiskField,
                },
              },
            ],
            [
              {
                label: t(
                  'client.legal.legalClientBeneficiary.mainInfo.capitalParticipationPercentage'
                ),
                value: mainInfo.capitalParticipationPercentage?.toString(),
              },
            ],
          ])
        : undefined,
    [isGlobal, mainInfo, t]
  );

  return { tabs, info };
};
